import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactFlow, {
  Background,
  Controls,
  ReactFlowProvider,
  addEdge,
  useEdgesState,
  useNodesState,
  useReactFlow
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  Typography,
  List,
  ListItemButton,
  ListItemText
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { Live2DStage } from '../live2d';
import { API_BASE_URL } from '../../utils/api';
import { buildLive2dModelUrlFromParts } from '../../utils/live2dUrls';
import { flattenMotions } from './flattenMotions';
import AnimStateNode from './AnimStateNode';
import { AnimationGraphContext } from './AnimationGraphContext';
import { DIALOGUE_AGGREGATE_EMOTION_KEYS } from '../../game/dialogueEmotionAggregates';

const PUBLIC = typeof process !== 'undefined' ? process.env.PUBLIC_URL || '' : '';

const nodeTypes = { live2dAnim: AnimStateNode };

function newAnimNodeId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return `anim-${crypto.randomUUID()}`;
  return `anim-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

const FLOW_EDGE_STROKE = '#52525b';
const FLOW_EDGE_WIDTH = 1;
const FLOW_EDGE_SELECTED_STROKE = '#9d8bc7';
const TRANSITION_PREVIEW_STEP_MS = 1200;

function normalizeAnimGraphEdges(edgeList) {
  if (!Array.isArray(edgeList)) return [];
  return edgeList.map((edge) => {
    const { selected: _sel, ...rest } = edge;
    return {
      ...rest,
      animated: false,
      style: { stroke: FLOW_EDGE_STROKE, strokeWidth: FLOW_EDGE_WIDTH, ...rest.style }
    };
  });
}

function motionIndexLabel(motionIndex) {
  return `#${String(Number(motionIndex) || 0).padStart(2, '0')}`;
}

function firstModelPathFromGraph(animGraph) {
  const nodes = animGraph?.nodes;
  if (!Array.isArray(nodes)) return '';
  const n = nodes.find((x) => x.type === 'live2dAnim' && x.data?.modelPath);
  return String(n?.data?.modelPath || '').trim();
}

function AnimControllerStudioInner({ row, onClose, onSaved, onNotify }) {
  const pack = row.live2dPack;
  const { setViewport } = useReactFlow();

  const [indexData, setIndexData] = useState(null);
  const [indexErr, setIndexErr] = useState('');
  const [modelPath, setModelPath] = useState('');
  const [modelJson, setModelJson] = useState(null);
  const [modelErr, setModelErr] = useState('');
  const [triggers, setTriggers] = useState([]);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [preview, setPreview] = useState(null);
  /** Same modelPath on source & target: two-step clip preview on edge click */
  const [edgeTransitionPreview, setEdgeTransitionPreview] = useState(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState(null);
  const [savingGraph, setSavingGraph] = useState(false);
  const [rfInstance, setRfInstance] = useState(null);
  const graphHydratedForIdRef = useRef(null);
  const modelPathInitForRowRef = useRef(null);

  useEffect(() => {
    modelPathInitForRowRef.current = null;
  }, [row._id]);

  useEffect(() => {
    let cancel = false;
    setIndexErr('');
    setIndexData(null);
    (async () => {
      try {
        const res = await fetch(`${PUBLIC}/live2d/${encodeURIComponent(pack)}/index.json`, {
          cache: 'no-store'
        });
        if (!res.ok) throw new Error('index');
        const data = await res.json();
        if (cancel) return;
        setIndexData(data);
      } catch {
        if (!cancel) setIndexErr('Failed to load index.json');
      }
    })();
    return () => {
      cancel = true;
    };
  }, [pack]);

  useEffect(() => {
    if (!indexData?.models?.length) return;
    const rid = String(row._id);
    if (modelPathInitForRowRef.current === rid) return;
    const fromGraph = firstModelPathFromGraph(row.animGraph);
    const paths = indexData.models.map((m) => m.path);
    const next = fromGraph && paths.includes(fromGraph) ? fromGraph : indexData.models[0].path;
    setModelPath(next);
    modelPathInitForRowRef.current = rid;
  }, [row._id, row.animGraph, indexData]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/triggers`);
        const data = await res.json();
        if (cancel) return;
        setTriggers(Array.isArray(data) ? data : []);
      } catch {
        if (!cancel) setTriggers([]);
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  useEffect(() => {
    if (!modelPath) {
      setModelJson(null);
      setModelErr('');
      return undefined;
    }
    const ac = new AbortController();
    setModelErr('');
    setModelJson(null);
    const url = buildLive2dModelUrlFromParts(PUBLIC, pack, modelPath);
    (async () => {
      try {
        const res = await fetch(url, { signal: ac.signal, cache: 'no-store' });
        if (!res.ok) throw new Error('model');
        const data = await res.json();
        if (ac.signal.aborted) return;
        setModelJson(data);
      } catch {
        if (ac.signal.aborted) return;
        setModelErr('Failed to load model');
      }
    })();
    return () => ac.abort();
  }, [pack, modelPath]);

  const motionRows = useMemo(() => flattenMotions(modelJson), [modelJson]);

  const sortedTriggers = useMemo(() => {
    return [...triggers].sort((a, b) => {
      const an = String(a.name || '').toLocaleLowerCase();
      const bn = String(b.name || '').toLocaleLowerCase();
      const byName = an.localeCompare(bn, undefined, { sensitivity: 'base' });
      if (byName !== 0) return byName;
      return String(a.key || '')
        .toLocaleLowerCase()
        .localeCompare(String(b.key || '').toLocaleLowerCase(), undefined, { sensitivity: 'base' });
    });
  }, [triggers]);

  useEffect(() => {
    const rid = String(row._id);
    if (graphHydratedForIdRef.current === rid) return;
    graphHydratedForIdRef.current = rid;
    const g = row.animGraph;
    const n = g?.nodes;
    const e = g?.edges;
    setNodes(Array.isArray(n) ? n : []);
    setEdges(normalizeAnimGraphEdges(e));
    const vp = g?.viewport;
    if (vp && typeof vp.x === 'number' && typeof vp.y === 'number' && typeof vp.zoom === 'number') {
      requestAnimationFrame(() => setViewport(vp));
    }
    // row.animGraph read only when switching controller (ref gate); avoid [row] — parent replaces object often
    // eslint-disable-next-line react-hooks/exhaustive-deps -- row._id
  }, [row._id, setNodes, setEdges, setViewport]);

  const applyPreviewFromClip = useCallback((clip) => {
    if (!clip?.modelPath) return;
    setEdgeTransitionPreview(null);
    setSelectedEdgeId(null);
    setPreview({
      modelPath: clip.modelPath,
      motionGroup: clip.motionGroup || 'idle',
      motionIndex: clip.motionIndex ?? 0
    });
  }, []);

  useEffect(() => {
    if (!modelPath || !motionRows.length) {
      setPreview(null);
      return;
    }
    // applyPreviewFromClip clears edge transition; skip while edge preview is active (e.g. model.json
    // finishes loading after the user clicked an edge — otherwise step 0 never reaches the timeout).
    if (edgeTransitionPreview) return;
    applyPreviewFromClip({
      modelPath,
      motionGroup: motionRows[0].motionGroup,
      motionIndex: motionRows[0].motionIndex
    });
  }, [modelPath, motionRows, applyPreviewFromClip, edgeTransitionPreview]);

  const previewModelUrl = useMemo(() => {
    if (!preview?.modelPath) return '';
    return buildLive2dModelUrlFromParts(PUBLIC, pack, preview.modelPath);
  }, [preview, pack]);

  const transitionStageUrl = useMemo(() => {
    if (!edgeTransitionPreview?.modelPath) return '';
    return buildLive2dModelUrlFromParts(PUBLIC, pack, edgeTransitionPreview.modelPath);
  }, [edgeTransitionPreview, pack]);

  useEffect(() => {
    if (!edgeTransitionPreview || edgeTransitionPreview.step !== 0) return undefined;
    const t = window.setTimeout(() => {
      setEdgeTransitionPreview((cur) => (cur && cur.step === 0 ? { ...cur, step: 1 } : cur));
    }, TRANSITION_PREVIEW_STEP_MS);
    return () => window.clearTimeout(t);
  }, [edgeTransitionPreview]);

  const displayEdges = useMemo(
    () =>
      edges.map((e) => {
        const selected = e.id === selectedEdgeId;
        return {
          ...e,
          selected,
          style: {
            stroke: selected ? FLOW_EDGE_SELECTED_STROKE : FLOW_EDGE_STROKE,
            strokeWidth: selected ? Math.max(FLOW_EDGE_WIDTH + 0.75, 2) : FLOW_EDGE_WIDTH,
            ...e.style
          }
        };
      }),
    [edges, selectedEdgeId]
  );

  const attachTrigger = useCallback(
    (nodeId, trigger) => {
      const tid = String(trigger._id);
      setNodes((nds) =>
        nds.map((n) => {
          if (n.id !== nodeId || n.type !== 'live2dAnim') return n;
          const cur = n.data?.triggerIds || [];
          if (cur.some((x) => String(x) === tid)) return n;
          return { ...n, data: { ...n.data, triggerIds: [...cur, tid] } };
        })
      );
    },
    [setNodes]
  );

  const attachEmotion = useCallback(
    (nodeId, key) => {
      setNodes((nds) =>
        nds.map((n) => {
          if (n.id !== nodeId || n.type !== 'live2dAnim') return n;
          const cur = n.data?.emotions || [];
          if (cur.includes(key)) return n;
          return { ...n, data: { ...n.data, emotions: [...cur, key] } };
        })
      );
    },
    [setNodes]
  );

  const detachTrigger = useCallback(
    (nodeId, triggerId) => {
      const hid = `t:${triggerId}`;
      setEdges((eds) => eds.filter((e) => !(e.source === nodeId && e.sourceHandle === hid)));
      setNodes((nds) =>
        nds.map((n) => {
          if (n.id !== nodeId || n.type !== 'live2dAnim') return n;
          const cur = n.data?.triggerIds || [];
          return {
            ...n,
            data: { ...n.data, triggerIds: cur.filter((id) => String(id) !== String(triggerId)) }
          };
        })
      );
    },
    [setNodes, setEdges]
  );

  const detachEmotion = useCallback(
    (nodeId, key) => {
      const hid = `e:${key}`;
      setEdges((eds) => eds.filter((e) => !(e.source === nodeId && e.sourceHandle === hid)));
      setNodes((nds) =>
        nds.map((n) => {
          if (n.id !== nodeId || n.type !== 'live2dAnim') return n;
          const cur = n.data?.emotions || [];
          return { ...n, data: { ...n.data, emotions: cur.filter((k) => k !== key) } };
        })
      );
    },
    [setNodes, setEdges]
  );

  const graphContextValue = useMemo(
    () => ({
      triggers,
      attachTrigger,
      attachEmotion,
      detachTrigger,
      detachEmotion
    }),
    [triggers, attachTrigger, attachEmotion, detachTrigger, detachEmotion]
  );

  const isValidConnection = useCallback(
    (connection) => {
      if (connection.targetHandle !== 'in') return false;
      const sh = connection.sourceHandle;
      if (!sh || sh === 'in') return false;
      if (sh !== 'done' && !sh.startsWith('t:') && !sh.startsWith('e:')) return false;
      return !edges.some(
        (e) =>
          e.source === connection.source &&
          e.sourceHandle === connection.sourceHandle &&
          e.target === connection.target
      );
    },
    [edges]
  );

  const onConnect = useCallback(
    (params) => {
      if (!isValidConnection(params)) return;
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            animated: false,
            style: { stroke: FLOW_EDGE_STROKE, strokeWidth: FLOW_EDGE_WIDTH }
          },
          eds
        )
      );
    },
    [setEdges, isValidConnection]
  );

  const onNodeClick = useCallback(
    (_, node) => {
      if (node.type !== 'live2dAnim' || !node.data?.modelPath) {
        setSelectedEdgeId(null);
        setEdgeTransitionPreview(null);
        return;
      }
      applyPreviewFromClip({
        modelPath: node.data.modelPath,
        motionGroup: node.data.motionGroup,
        motionIndex: node.data.motionIndex
      });
    },
    [applyPreviewFromClip]
  );

  const onPaneClick = useCallback(() => {
    setSelectedEdgeId(null);
    setEdgeTransitionPreview(null);
  }, []);

  const onEdgeClick = useCallback(
    (_, edge) => {
      setSelectedEdgeId(edge.id);
      const src = nodes.find((n) => n.id === edge.source);
      const tgt = nodes.find((n) => n.id === edge.target);
      if (!src || !tgt || src.type !== 'live2dAnim' || tgt.type !== 'live2dAnim') {
        setEdgeTransitionPreview(null);
        return;
      }
      const p1 = src.data?.modelPath;
      const p2 = tgt.data?.modelPath;
      if (!p1 || p1 !== p2) {
        setEdgeTransitionPreview(null);
        onNotify?.(
          'Превью перехода: с обеих сторон должна быть одна и та же модель (один .model3 в index).',
          'warning'
        );
        return;
      }
      setPreview(null);
      setEdgeTransitionPreview({
        modelPath: p1,
        from: {
          motionGroup: src.data.motionGroup || 'idle',
          motionIndex: src.data.motionIndex ?? 0,
          label: src.data.label || ''
        },
        to: {
          motionGroup: tgt.data.motionGroup || 'idle',
          motionIndex: tgt.data.motionIndex ?? 0,
          label: tgt.data.label || ''
        },
        step: 0
      });
    },
    [nodes, onNotify]
  );

  const onDropOnPane = useCallback(
    (event) => {
      event.preventDefault();
      const raw = event.dataTransfer.getData('application/anim-clip');
      if (!raw || !rfInstance) return;
      let clip;
      try {
        clip = JSON.parse(raw);
      } catch {
        return;
      }
      if (!clip?.modelPath) return;
      const position = rfInstance.screenToFlowPosition({ x: event.clientX, y: event.clientY });
      const newNode = {
        id: newAnimNodeId(),
        type: 'live2dAnim',
        position,
        data: {
          modelPath: clip.modelPath,
          motionGroup: clip.motionGroup,
          motionIndex: clip.motionIndex,
          label: clip.label || '',
          triggerIds: [],
          emotions: []
        }
      };
      setNodes((nds) => [...nds, newNode]);
    },
    [rfInstance, setNodes]
  );

  const onDragOverPane = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  }, []);

  const saveGraph = async () => {
    setSavingGraph(true);
    try {
      const viewport = rfInstance?.getViewport?.() || { x: 0, y: 0, zoom: 1 };
      const animGraph = {
        nodes,
        edges: normalizeAnimGraphEdges(edges),
        viewport: { x: viewport.x, y: viewport.y, zoom: viewport.zoom }
      };
      const res = await fetch(`${API_BASE_URL}/api/live2d-anim-controllers/${row._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ animGraph })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || 'Save failed');
      onNotify?.('Animation graph saved');
      await onSaved?.(data);
    } catch (e) {
      onNotify?.(e.message || 'Save failed', 'error');
    } finally {
      setSavingGraph(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      fullScreen
      scroll="paper"
      PaperProps={{
        sx: {
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          maxHeight: '100%',
          m: 0,
          borderRadius: 0,
          bgcolor: 'background.default'
        }
      }}
    >
      <DialogTitle
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          py: 1.25,
          pr: 1,
          borderBottom: 1,
          borderColor: 'divider'
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography component="span" variant="subtitle1" fontWeight={600}>
            Animations · {row.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" fontFamily="monospace" display="block" noWrap title={row.indexUrl || `/live2d/${pack}/index.json`}>
            {row.indexUrl || `/live2d/${pack}/index.json`}
          </Typography>
        </Box>
        <Tooltip title="Exit studio">
          <IconButton edge="end" onClick={onClose} aria-label="close" size="small">
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          p: 2,
          pt: 1.5,
          overflow: 'hidden'
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, flexShrink: 0 }}>
          <FormControl size="small" sx={{ minWidth: 280, maxWidth: 520, flex: '1 1 280px' }}>
            <InputLabel id="studio-model-label">Live2D model (from index.json)</InputLabel>
            <Select
              labelId="studio-model-label"
              label="Live2D model (from index.json)"
              value={modelPath}
              onChange={(e) => setModelPath(e.target.value)}
              MenuProps={{
                MenuListProps: {
                  className: 'vr-scroll-y',
                  sx: { maxHeight: 360 }
                }
              }}
            >
              {(indexData?.models || []).map((m) => (
                <MenuItem key={m.path} value={m.path}>
                  {m.id || m.path}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ flex: '1 1 200px', minWidth: 0 }}>
            Drag outputs to another node for transitions. One link per trigger / onComplete / emotion.
          </Typography>
        </Box>
        {indexErr ? (
          <Typography variant="body2" color="error" sx={{ flexShrink: 0 }}>
            {indexErr}
          </Typography>
        ) : null}

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(340px, 34vw) minmax(0, 1fr) minmax(260px, 24vw)' },
            gridTemplateRows: { xs: 'minmax(240px, 35vh) minmax(280px, 40vh) minmax(200px, 25vh)', lg: '1fr' },
            gap: 2,
            alignItems: 'stretch'
          }}
        >
          <Paper
            variant="outlined"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              height: { lg: '100%' },
              overflow: 'hidden'
            }}
          >
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) minmax(0, 1fr)' },
                gridTemplateRows: { xs: 'minmax(200px, 42vh) minmax(160px, 38vh)', lg: '1fr' },
                gap: 0
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  minWidth: 0,
                  borderRight: { lg: 1 },
                  borderBottom: { xs: 1, lg: 0 },
                  borderColor: 'divider'
                }}
              >
                <Box sx={{ px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                    Анимации
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                    Перетащите клип на граф
                  </Typography>
                </Box>
                <Box className="vr-scroll-y" sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                  {!modelPath ? (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                      Выберите модель
                    </Typography>
                  ) : modelErr ? (
                    <Typography variant="body2" color="error" sx={{ p: 1 }}>
                      {modelErr}
                    </Typography>
                  ) : !motionRows.length ? (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                      В модели нет motion
                    </Typography>
                  ) : (
                    <List dense disablePadding>
                      {motionRows.map((rowItem) => {
                        const clipPayload = JSON.stringify({
                          modelPath,
                          motionGroup: rowItem.motionGroup,
                          motionIndex: rowItem.motionIndex,
                          label: rowItem.label
                        });
                        const selected =
                          preview &&
                          preview.modelPath === modelPath &&
                          preview.motionGroup === rowItem.motionGroup &&
                          preview.motionIndex === rowItem.motionIndex;
                        const idxLabel = motionIndexLabel(rowItem.motionIndex);
                        const clipTitle = `${rowItem.motionGroup} · ${idxLabel} ${rowItem.label}`;
                        return (
                          <ListItemButton
                            key={`${rowItem.motionGroup}-${rowItem.motionIndex}-${rowItem.file}`}
                            selected={!!selected}
                            draggable
                            title={clipTitle}
                            onDragStart={(e) => {
                              e.dataTransfer.setData('application/anim-clip', clipPayload);
                              e.dataTransfer.effectAllowed = 'copy';
                            }}
                            onClick={() => applyPreviewFromClip({ modelPath, ...rowItem })}
                            sx={{ py: 0.5 }}
                          >
                            <ListItemText
                              primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                              primary={
                                <Box component="span" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  <Typography
                                    component="span"
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ fontVariantNumeric: 'tabular-nums', mr: 0.75 }}
                                  >
                                    {idxLabel}
                                  </Typography>
                                  <Typography component="span" variant="body2">
                                    {rowItem.label}
                                  </Typography>
                                </Box>
                              }
                            />
                          </ListItemButton>
                        );
                      })}
                    </List>
                  )}
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 }}>
                <Box sx={{ px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                    Триггеры и эмоции
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                    Перетащите на ноду графа
                  </Typography>
                </Box>
                <Box className="vr-scroll-y" sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                  {sortedTriggers.length === 0 ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', p: 1 }}>
                      Нет триггеров (админка → Triggers)
                    </Typography>
                  ) : (
                    <List dense disablePadding>
                      {sortedTriggers.map((t) => (
                        <ListItemButton
                          key={t._id}
                          dense
                          draggable
                          title={`${t.name} (${t.key})`}
                          onDragStart={(e) => {
                            e.dataTransfer.setData(
                              'application/trigger',
                              JSON.stringify({ _id: t._id, key: t.key, name: t.name })
                            );
                            e.dataTransfer.effectAllowed = 'copy';
                          }}
                          sx={{ py: 0.2, px: 0.75, minHeight: 0, alignItems: 'flex-start' }}
                        >
                          <ListItemText
                            disableTypography
                            sx={{ my: 0 }}
                            primary={
                              <Typography
                                variant="caption"
                                component="div"
                                noWrap
                                sx={{ fontSize: '0.7rem', lineHeight: 1.25, display: 'block' }}
                              >
                                {t.name}
                                <Typography
                                  component="span"
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontSize: '0.65rem', ml: 0.4, fontFamily: 'monospace' }}
                                >
                                  {t.key}
                                </Typography>
                              </Typography>
                            }
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  )}
                </Box>
                <Box sx={{ flexShrink: 0, borderTop: 1, borderColor: 'divider', px: 0.75, py: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
                    Эмоции
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {DIALOGUE_AGGREGATE_EMOTION_KEYS.map((key) => (
                      <Paper
                        key={key}
                        variant="outlined"
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData('application/emotion', JSON.stringify({ key }));
                          e.dataTransfer.effectAllowed = 'copy';
                        }}
                        sx={{
                          px: 1,
                          py: 0.5,
                          cursor: 'grab',
                          typography: 'caption',
                          bgcolor: 'rgba(156, 39, 176, 0.08)',
                          borderColor: 'secondary.dark'
                        }}
                      >
                        {key}
                      </Paper>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Paper>

          <Paper
            variant="outlined"
            sx={{
              height: { lg: '100%' },
              minHeight: 0,
              bgcolor: 'grey.900',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              '& .react-flow__attribution': { display: 'none' },
              '& .react-flow__edge-path, & .react-flow__connection-path': {
                stroke: FLOW_EDGE_STROKE,
                strokeWidth: `${FLOW_EDGE_WIDTH}px`
              },
              '& .react-flow__edge.animated path': {
                animation: 'none',
                strokeDasharray: 'none'
              },
              '& .react-flow__connection .animated': {
                animation: 'none',
                strokeDasharray: 'none'
              },
              '& .react-flow__handle': {
                width: 5,
                height: 5,
                borderColor: FLOW_EDGE_STROKE,
                background: '#27272a'
              }
            }}
          >
            <AnimationGraphContext.Provider value={graphContextValue}>
              <Box sx={{ flex: 1, minHeight: 0, position: 'relative', width: '100%' }}>
                <ReactFlow
                  style={{ width: '100%', height: '100%' }}
                  nodes={nodes}
                  edges={displayEdges}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  onConnect={onConnect}
                  isValidConnection={isValidConnection}
                  onInit={setRfInstance}
                  onDrop={onDropOnPane}
                  onDragOver={onDragOverPane}
                  onPaneClick={onPaneClick}
                  onNodeClick={onNodeClick}
                  onEdgeClick={onEdgeClick}
                  nodeTypes={nodeTypes}
                  onlyRenderVisibleElements
                  elevateNodesOnSelect={false}
                  connectionLineStyle={{ stroke: FLOW_EDGE_STROKE, strokeWidth: FLOW_EDGE_WIDTH }}
                  defaultEdgeOptions={{
                    animated: false,
                    style: { stroke: FLOW_EDGE_STROKE, strokeWidth: FLOW_EDGE_WIDTH }
                  }}
                  proOptions={{ hideAttribution: true }}
                  deleteKeyCode={['Backspace', 'Delete']}
                >
                  <Background gap={16} color="#333" />
                  <Controls />
                </ReactFlow>
              </Box>
            </AnimationGraphContext.Provider>
          </Paper>

          <Paper
            variant="outlined"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: { lg: '100%' },
              minHeight: 0,
              bgcolor: 'grey.900',
              overflow: 'hidden'
            }}
          >
            <Box sx={{ px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                Preview
              </Typography>
              {edgeTransitionPreview && transitionStageUrl ? (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25, lineHeight: 1.25 }}>
                  Переход: {edgeTransitionPreview.from.label || 'A'} → {edgeTransitionPreview.to.label || 'B'} (
                  {edgeTransitionPreview.step === 0 ? 'исход' : 'цель'})
                </Typography>
              ) : null}
            </Box>
            <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
              {edgeTransitionPreview && transitionStageUrl ? (
                <Live2DStage
                  key={`tr-${transitionStageUrl}`}
                  className="min-h-0 min-w-0 h-full w-full"
                  modelUrl={transitionStageUrl}
                  motionGroup={
                    edgeTransitionPreview.step === 0
                      ? edgeTransitionPreview.from.motionGroup
                      : edgeTransitionPreview.to.motionGroup
                  }
                  motionIndex={
                    edgeTransitionPreview.step === 0
                      ? edgeTransitionPreview.from.motionIndex
                      : edgeTransitionPreview.to.motionIndex
                  }
                  motionLoop={edgeTransitionPreview.step !== 0}
                  speedMultiplier={1}
                  backgroundColor={0x141418}
                />
              ) : previewModelUrl ? (
                <Live2DStage
                  key={`${previewModelUrl}-${preview.motionGroup}-${preview.motionIndex}`}
                  className="min-h-0 min-w-0 h-full w-full"
                  modelUrl={previewModelUrl}
                  motionGroup={preview.motionGroup}
                  motionIndex={preview.motionIndex}
                  speedMultiplier={1}
                  backgroundColor={0x141418}
                />
              ) : (
                <Box sx={{ p: 2, color: 'text.secondary' }}>
                  <Typography variant="body2">Клип из списка, нода или связь (одна модель)</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      </DialogContent>
      <DialogActions sx={{ flexShrink: 0, flexWrap: 'wrap', gap: 1, px: 2, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" onClick={saveGraph} disabled={savingGraph}>
          Save graph
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function AnimControllerStudio(props) {
  return (
    <ReactFlowProvider>
      <AnimControllerStudioInner {...props} />
    </ReactFlowProvider>
  );
}
